import Reveal from "@/components/Reveal";
import useSEO from "@/lib/useSEO";

const SECTIONS = [
  {
    title: "What we collect",
    body: "When you order or create an account we collect your name, email address, and shipping address — the minimum needed to print a shirt and get it to your door. If you sign in with Google, we receive your name, email, and profile photo from Google, and nothing else.",
  },
  {
    title: "Who touches your data",
    body: "Payments are handled entirely by Stripe — your card number never touches our servers. Orders are printed and shipped by Printify, which receives your name, shipping address and email address (used for order issues and delivery, nothing else). Sign-in is provided by Google. Order emails are sent through our email provider. The site itself is hosted by Emergent, which processes standard hosting data (pages served, uptime, aggregate usage). That's the whole list.",
  },
  {
    title: "What we never do",
    body: "We do not sell, rent, or trade your personal information to anyone, for any reason. We do not run advertising or marketing trackers of our own, and we do not build profiles. Politics aside, your data is not the product.",
  },
  {
    title: "Cookies",
    body: "This store sets one essential cookie: a sign-in session token that keeps you logged in for up to 7 days. Our hosting platform (Emergent) sets its own essential session cookie and loads platform scripts for uptime monitoring, surveys, and aggregate usage measurement — those are governed by Emergent's privacy policy, not ours. Stripe sets fraud-prevention cookies on its secure checkout page, and Google does the same if you sign in with Google. No ad-network cookies, no cross-site tracking pixels, and nothing about you is ever sold.",
  },
  {
    title: "Your rights",
    body: (
      <>
        Want a copy of your data, a correction, or full deletion? Email{" "}
        <a href="mailto:yell@fleecetheregime.com" className="font-bold text-black underline underline-offset-2 hover:text-primary transition-colors">
          yell@fleecetheregime.com
        </a>{" "}
        and we'll handle it within 30 days, no forms, no runaround. Deleting your account removes your profile and sign-in sessions; completed order records are kept as required for tax and accounting.
      </>
    ),
  },
  {
    title: "Changes & contact",
    body: "If this policy changes we'll update this page with the date. Questions about privacy go to yell@fleecetheregime.com. Last updated September 2026.",
  },
];

export default function Privacy() {
  useSEO(
    "Privacy Policy | Fleece The Regime",
    "Our privacy policy: what we collect (very little), who touches it (Stripe, Printify, Google), and what we never do (sell your data).",
    false,
    "/privacy/"
  );
  return (
    <main data-testid="privacy-page" className="pt-16 min-h-screen">
      <section className="px-6 sm:px-12 lg:px-20 pt-20 pb-14 border-b-2 border-black">
        <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-primary mb-6">Privacy Policy</p>
        <h1 className="font-display uppercase text-6xl sm:text-7xl lg:text-8xl leading-[0.85] tracking-tighter" data-testid="privacy-heading">
          Your data<br />stays <span className="text-primary">yours.</span>
        </h1>
        <p className="mt-8 max-w-xl font-mono text-sm leading-relaxed text-black/60">
          The short version: we collect the minimum to print and ship your order, and we never sell anything about you to anyone.
        </p>
      </section>
      <section className="px-6 sm:px-12 lg:px-20 py-16 max-w-5xl">
        {SECTIONS.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.05}>
            <div className="border-t-2 border-black py-10" data-testid={`privacy-section-${i}`}>
              <h2 className="font-display uppercase text-2xl sm:text-3xl tracking-tight">
                <span className="text-primary mr-4">{String(i + 1).padStart(2, "0")}</span>
                {s.title}
              </h2>
              <p className="mt-4 max-w-2xl font-mono text-sm leading-relaxed text-black/70">{s.body}</p>
            </div>
          </Reveal>
        ))}
        <p className="mt-10 font-mono text-xs text-black/40 leading-relaxed">
          This page is store policy, not legal advice. For the real thing, talk to a privacy attorney.
        </p>
      </section>
    </main>
  );
}
